import * as React from 'react';
import { Badge, Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { ArrowRight, CheckCircle, Scales, WarningCircle } from 'phosphor-react';

import { GlassCard } from '../../../../../../components/glass';
import {
  IDivergenciaRegra,
  useContaCilDivergenciasRazao,
} from '../../../../../../hooks/useContaCilSchoolAi';

const C = {
  violet: '#8B5CF6',
  amber: '#F59E0B',
  green: '#10B981',
  red: '#EF4444',
  ink: '#0F172A',
  muted: '#64748B',
  subtle: '#94A3B8',
};

function MiniKpi({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <GlassCard intensity="medium" p={4}>
      <Text fontSize="26px" fontWeight="700" color={color} lineHeight="1">
        {value}
      </Text>
      <Text fontSize="11px" color={C.muted} mt={1}>
        {label}
      </Text>
    </GlassCard>
  );
}

function ItemDivergencia({ it }: { it: IDivergenciaRegra }) {
  const diverge = it.veredito === 'DIVERGE';
  return (
    <Flex
      align="center"
      gap={3}
      px={3}
      py={2.5}
      borderRadius="lg"
      _hover={{ bg: 'rgba(15,23,42,0.03)' }}
    >
      {diverge ? (
        <WarningCircle size={18} weight="fill" color={C.red} />
      ) : (
        <CheckCircle size={18} weight="fill" color={C.subtle} />
      )}
      <Box flex="1" minW={0}>
        <Flex align="center" gap={2}>
          <Text fontSize="13px" color={C.ink} fontWeight="600" noOfLines={1}>
            {it.categoria}
          </Text>
          {it.escopo === 'global' ? (
            <Badge borderRadius="full" fontSize="9px" colorScheme="purple">
              global
            </Badge>
          ) : null}
          {it.criada_por_ia ? (
            <Badge borderRadius="full" fontSize="9px" colorScheme="cyan">
              IA
            </Badge>
          ) : null}
        </Flex>
        {it.kw?.length ? (
          <Text fontSize="11px" color={C.muted} noOfLines={1}>
            {it.kw.join(' · ')}
          </Text>
        ) : null}
      </Box>

      {/* regra → razão */}
      <Flex align="center" gap={2} flexShrink={0}>
        <Text fontSize="12px" color={C.muted} title="conta que a regra atribui">
          {it.poc_regra}
        </Text>
        <ArrowRight size={12} weight="bold" color={C.subtle} />
        <Text
          fontSize="12px"
          fontWeight="600"
          color={diverge ? C.red : C.green}
          title="conta dominante no razão (o que o contador usou)"
        >
          {it.poc_razao_dominante ?? '—'}
        </Text>
      </Flex>

      <Text fontSize="11px" color={C.subtle} w="92px" textAlign="right" flexShrink={0}>
        {it.veredito === 'SEM_DADOS'
          ? 'sem amostra'
          : `${Math.round(it.pureza * 100)}% · ${it.suporte_dominante}/${it.total_postings}`}
      </Text>
    </Flex>
  );
}

/**
 * Divergências regra × razão (F4.5 gap #2): o razão como professor. Lista as
 * regras cuja conta diverge da que o contador realmente usou no razão — lista
 * de revisão. Corrigir/desativar a regra é na tela de Regras (não aqui).
 */
export function DivergenciasTab({ empresaId }: { empresaId: number }) {
  const q = useContaCilDivergenciasRazao(empresaId);
  const [verTodas, setVerTodas] = React.useState(false);

  if (q.isLoading) {
    return (
      <GlassCard intensity="medium" p={10}>
        <Flex align="center" justify="center" gap={3}>
          <Spinner size="sm" color={C.violet} />
          <Text fontSize="sm" color={C.muted}>
            Confrontando as regras com o razão…
          </Text>
        </Flex>
      </GlassCard>
    );
  }

  if (q.isError) {
    return (
      <GlassCard intensity="medium" p={8}>
        <Text fontSize="sm" color={C.ink} fontWeight="600" textAlign="center">
          Não foi possível analisar as divergências. Recarregue a página.
        </Text>
      </GlassCard>
    );
  }

  const d = q.data;
  if (!d || d.total_regras === 0) {
    return (
      <GlassCard intensity="medium" p={10}>
        <Flex direction="column" align="center" gap={2}>
          <Scales size={28} weight="fill" color={C.subtle} />
          <Text fontSize="sm" color={C.ink} fontWeight="600">
            Nenhuma regra com conta fixa para confrontar
          </Text>
          <Text fontSize="12px" color={C.muted} textAlign="center">
            Esta empresa não tem regras dinâmicas com POC fixo, ou não há razão extraído.
          </Text>
        </Flex>
      </GlassCard>
    );
  }

  // DIVERGE sempre visível; CONFIRMADA/SEM_DADOS sob "ver todas".
  const visiveis = verTodas ? d.itens : d.itens.filter(i => i.veredito === 'DIVERGE');

  return (
    <Box>
      <Flex gap={3} mb={4} wrap="wrap">
        <Box flex="1" minW="120px">
          <MiniKpi label="Divergem (revisar)" value={d.divergem} color={C.red} />
        </Box>
        <Box flex="1" minW="120px">
          <MiniKpi label="Confirmadas" value={d.confirmadas} color={C.green} />
        </Box>
        <Box flex="1" minW="120px">
          <MiniKpi label="Sem amostra" value={d.sem_dados} color={C.subtle} />
        </Box>
      </Flex>

      <GlassCard intensity="medium" p={{ base: 4, md: 5 }}>
        <Flex align="center" justify="space-between" mb={3} gap={2} wrap="wrap">
          <Flex align="center" gap={2}>
            <Scales size={18} weight="fill" color={C.violet} />
            <Text fontSize="md" fontWeight="700" color={C.ink}>
              Regra × razão
            </Text>
          </Flex>
          <Text
            as="button"
            fontSize="12px"
            fontWeight="600"
            color={C.violet}
            onClick={() => setVerTodas(v => !v)}
          >
            {verTodas ? 'Mostrar só divergências' : 'Ver todas as regras'}
          </Text>
        </Flex>
        <Text fontSize="12px" color={C.muted} mb={3}>
          regra atribui <ArrowRight size={10} weight="bold" style={{ display: 'inline' }} /> conta
          dominante no razão. Vermelho = revisar a regra na tela de Regras.
        </Text>

        {visiveis.length === 0 ? (
          <Flex direction="column" align="center" gap={2} py={6}>
            <CheckCircle size={26} weight="fill" color={C.green} />
            <Text fontSize="sm" color={C.ink} fontWeight="600">
              Nenhuma regra divergindo do razão
            </Text>
            <Text fontSize="12px" color={C.muted}>
              As regras com conta fixa batem com o que o contador usa.
            </Text>
          </Flex>
        ) : (
          <Box>
            {visiveis.map(it => (
              <ItemDivergencia key={it.regra_id} it={it} />
            ))}
          </Box>
        )}
      </GlassCard>
    </Box>
  );
}
